import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  ParquetFormState,
  ValidationErrors,
  EMPTY_FORM,
  MAX_COINS,
  COIN_GENESIS as COIN_GENESIS_FALLBACK,
  DataSource,
} from '../types';
import { useCoinRegistry } from '../../../shared/CoinRegistryContext';
import TimeframeSelector from './TimeframeSelector';
import DataTypeSelector from './DataTypeSelector';
import CoinSelector from './CoinSelector';
import DateRangePicker from './DateRangePicker';
import OutputPreview from './OutputPreview';

interface CreateParquetModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (form: ParquetFormState) => void;
  onSaveOnly?: (form: ParquetFormState) => void;
  initialValues?: ParquetFormState;
  loading?: boolean;
  mode?: 'create' | 'edit';
  existingNames?: string[];
}

type FieldKey = 'name' | 'timeframes' | 'dataTypes' | 'coins' | 'dateRange';

const ERROR_KEY_ORDER: FieldKey[] = [
  'name',
  'timeframes',
  'dataTypes',
  'coins',
  'dateRange',
];

interface CoinWarning {
  coin: string;
  genesis: string;
}

function validate(
  form: ParquetFormState,
  existingNames: string[],
  mode: 'create' | 'edit',
  originalName?: string,
): ValidationErrors {
  const errors: ValidationErrors = {};
  const trimmed = form.name.trim();
  const today = new Date().toISOString().slice(0, 10);

  if (!trimmed) {
    errors.name = 'Name is required';
  } else {
    const isDuplicate = existingNames.some(
      (n) =>
        n.toLowerCase() === trimmed.toLowerCase() &&
        (mode === 'create' ||
          n.toLowerCase() !== (originalName ?? '').toLowerCase()),
    );
    if (isDuplicate) {
      errors.name = `"${trimmed}" already exists. Choose a different name.`;
    }
  }

  if (form.timeframes.length === 0)
    errors.timeframes = 'Select at least 1 timeframe';
  if (form.dataTypes.length === 0)
    errors.dataTypes = 'Select at least 1 data type';
  if (form.coins.length === 0) errors.coins = 'Select at least 1 coin';
  if (form.coins.length > MAX_COINS)
    errors.coins = `Maximum ${MAX_COINS} coins allowed`;

  if (!form.dateFrom || !form.dateTo) {
    errors.dateRange = 'Both dates are required';
  } else if (form.dateFrom > today) {
    errors.dateRange = "'From' date cannot be in the future";
  } else if (form.dateTo > today) {
    errors.dateRange = "'To' date cannot be in the future";
  } else if (form.dateFrom >= form.dateTo) {
    errors.dateRange = "'From' must be before 'To'";
  }

  return errors;
}

function getCoinWarnings(
  coins: string[],
  dateFrom: string,
  genesisMap: Record<string, string>,
): CoinWarning[] {
  if (!dateFrom) return [];
  return coins
    .filter((c) => {
      const genesis = genesisMap[c];
      return genesis && dateFrom < genesis;
    })
    .map((c) => ({ coin: c, genesis: genesisMap[c] }));
}

function CreateParquetModal({
  open,
  onClose,
  onSubmit,
  onSaveOnly,
  initialValues,
  loading,
  mode = 'create',
  existingNames = [],
}: CreateParquetModalProps) {
  const { coinGenesis: registryGenesis } = useCoinRegistry();
  const COIN_GENESIS =
    Object.keys(registryGenesis).length > 0
      ? registryGenesis
      : COIN_GENESIS_FALLBACK;

  const [form, setForm] = useState<ParquetFormState>(
    initialValues ?? EMPTY_FORM,
  );
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [shaking, setShaking] = useState(false);
  const fieldRefs: Record<FieldKey, React.RefObject<HTMLDivElement>> = {
    name: useRef<HTMLDivElement>(null),
    timeframes: useRef<HTMLDivElement>(null),
    dataTypes: useRef<HTMLDivElement>(null),
    coins: useRef<HTMLDivElement>(null),
    dateRange: useRef<HTMLDivElement>(null),
  };

  const modalRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (open) {
      setForm(initialValues ?? EMPTY_FORM);
      setErrors({});
      setShaking(false);
    }
  }, [open, initialValues]);

  const triggerShake = useCallback(() => {
    setShaking(true);
    setTimeout(() => setShaking(false), 500);
  }, []);

  const scrollToFirstError = useCallback(
    (errs: ValidationErrors) => {
      for (const key of ERROR_KEY_ORDER) {
        if (errs[key] && fieldRefs[key].current) {
          fieldRefs[key].current!.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          break;
        }
      }
    },
    [fieldRefs],
  );

  const runValidation = (): boolean => {
    const errs = validate(form, existingNames, mode, initialValues?.name);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      triggerShake();
      setTimeout(() => scrollToFirstError(errs), 80);
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (runValidation()) onSubmit(form);
  };

  const handleSaveOnly = () => {
    if (onSaveOnly && runValidation()) onSaveOnly(form);
  };

  // Non-blocking warnings — computed live as the form changes
  const coinWarnings = useMemo(
    () => getCoinWarnings(form.coins, form.dateFrom, COIN_GENESIS),
    [form.coins, form.dateFrom, COIN_GENESIS],
  );

  const hasWarnings = coinWarnings.length > 0;

  if (!open) return null;

  return (
    <div className="pq-modal-overlay" onClick={onClose}>
      <div
        ref={modalRef}
        className={`pq-modal pq-modal--wide ${shaking ? 'pq-shake' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pq-modal-header">
          <h2>{mode === 'edit' ? 'Edit Row Tab' : 'Create Parquet File'}</h2>
          <button className="pq-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="pq-modal-body">
          {/* Name */}
          <div className="pq-field" ref={fieldRefs.name}>
            <label className="pq-label">
              Parquet File Name <span className="pq-required">*</span>
            </label>
            <input
              type="text"
              className={`pq-input ${errors.name ? 'has-error' : ''}`}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Enter file name..."
            />
            {errors.name && (
              <span className="pq-error pq-error--animate">{errors.name}</span>
            )}
          </div>

          {/* Timeframes */}
          <div ref={fieldRefs.timeframes}>
            <TimeframeSelector
              selected={form.timeframes}
              onChange={(timeframes) => setForm({ ...form, timeframes })}
              error={errors.timeframes}
            />
          </div>

          {/* Data Source */}
          <div className="pq-field">
            <label className="pq-label">Data Source</label>
            <div className="pq-datasource-select">
              <select
                className="pq-datasource-dropdown"
                value={form.dataSource}
                onChange={(e) =>
                  setForm({ ...form, dataSource: e.target.value as DataSource })
                }
              >
                <option value="cleaned">Cleaned (Spikes Removed)</option>
                <option value="spikes">With Spikes (Raw Data)</option>
              </select>
              <span className="pq-datasource-arrow">{'\u25BC'}</span>
            </div>
          </div>

          {/* Data Types */}
          <div ref={fieldRefs.dataTypes}>
            <DataTypeSelector
              selected={form.dataTypes}
              onChange={(dataTypes) => setForm({ ...form, dataTypes })}
              error={errors.dataTypes}
            />
          </div>

          {/* Coins */}
          <div ref={fieldRefs.coins}>
            <CoinSelector
              selected={form.coins}
              onChange={(coins) => setForm({ ...form, coins })}
              error={errors.coins}
            />
          </div>

          {/* Date Range */}
          <div ref={fieldRefs.dateRange}>
            <DateRangePicker
              dateFrom={form.dateFrom}
              dateTo={form.dateTo}
              onChangeFrom={(dateFrom) => setForm({ ...form, dateFrom })}
              onChangeTo={(dateTo) => setForm({ ...form, dateTo })}
              error={errors.dateRange}
            />
          </div>

          {/* Non-blocking warnings */}
          {hasWarnings && (
            <div className="pq-warnings">
              <div className="pq-warning-header">
                <span className="pq-warning-icon">{'\u26A0'}</span>
                <span>Data availability notice</span>
              </div>
              {coinWarnings.map((w) => (
                <div key={w.coin} className="pq-warning-row">
                  <strong>{w.coin}</strong> — data starts from{' '}
                  <span className="pq-warning-date">{w.genesis}</span>. No data
                  available between{' '}
                  <span className="pq-warning-date">{form.dateFrom}</span> and{' '}
                  <span className="pq-warning-date">{w.genesis}</span>.
                </div>
              ))}
            </div>
          )}

          {/* Output Preview */}
          <OutputPreview form={form} />
        </div>

        <div className="pq-modal-footer">
          <button
            className="pq-btn pq-btn-ghost"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          {onSaveOnly && (
            <button
              className="pq-btn pq-btn-save-only"
              onClick={handleSaveOnly}
              disabled={loading}
            >
              {mode === 'edit' ? 'Save Preset' : 'Save Preset'}
            </button>
          )}
          <button
            className="pq-btn pq-btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading
              ? 'Generating...'
              : mode === 'edit'
                ? 'Save & Download'
                : 'Create & Download'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateParquetModal;
